import type { NormalizedProfile, RawProfilePoint } from '@/types/profile';
import { convertLength, detectUnit } from '@/lib/utils/units';

/**
 * Converts a trimmed profile into a normalized, geometry-ready form:
 *  - Sorted by position, ascending.
 *  - Position re-based so the profile starts at 0 (knob end).
 *  - Duplicate/near-duplicate positions collapsed (keeps the lathe's actual
 *    average radius at that station rather than feeding LatheGeometry a
 *    zero-length segment).
 *  - Converted to millimeters internally, regardless of source unit, so the
 *    rest of the app (geometry, metrics, exports) never has to branch on units.
 */
export function normalizeProfile(trimmedPoints: RawProfilePoint[]): NormalizedProfile {
  const unit = detectUnit(trimmedPoints);

  const sorted = [...trimmedPoints].sort((a, b) => a.position - b.position);
  const minPos = sorted.length > 0 ? sorted[0].position : 0;

  // Collapse near-duplicate stations (within 1e-4 of the source unit) by averaging radius.
  const EPS = 1e-4;
  const collapsed: RawProfilePoint[] = [];
  for (const p of sorted) {
    const rebasedPosition = p.position - minPos;
    const last = collapsed[collapsed.length - 1];
    if (last && Math.abs(rebasedPosition - last.position) < EPS) {
      last.radius = (last.radius + p.radius) / 2;
    } else {
      collapsed.push({ index: collapsed.length, position: rebasedPosition, radius: p.radius });
    }
  }

  const mmPoints: RawProfilePoint[] = collapsed.map((p, i) => ({
    index: i,
    position: convertLength(p.position, unit, 'mm'),
    radius: convertLength(p.radius, unit, 'mm'),
  }));

  return { points: mmPoints, unit };
}
